import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Table, Modal } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  Button,
  PageHeader,
  Pagination,
} from '@cloudcar-app/cloudcar-components';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import { useLazyQuery } from '@apollo/client';
import {
  Car,
  LitePurchaseIntent,
  LitePurchaseIntentConnection,
  LitePurchaseIntentCursor,
  LitePurchaseIntentCursorInput,
} from 'apollo/types';
import operations from 'apollo/operations';
import dateFormatter from 'utils/dateFormatter';
import { getDealerFromIdToken } from 'utils/getDataFromJwt';
import { SearchOutlined } from '@ant-design/icons';
import { PurchaseIntentSearch } from 'components/purchaseIntentList';
import styles from './PurchaseIntentList.module.scss';

const InsuranceProposal: FC = () => {
  const history = useHistory();

  const [nextCursor, setNextCursor] = useState<
    LitePurchaseIntentCursor | undefined | null
  >(undefined);

  const [pageSize, setPageSize] = useState<number>(5);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(-1);
  const [cursorList, setCursorList] = useState<LitePurchaseIntentCursor[]>([]);
  const [hasPrevPage, setHasPrevPage] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [litePurchaseIntentList, setLitePurchaseIntentList] = useState<
    null | LitePurchaseIntent[]
  >(null);

  const [listPurchaseIntent, { data: liteData, loading: liteLoading }] =
    useLazyQuery<{
      paginateLitePurchaseIntent: LitePurchaseIntentConnection;
    }>(operations.GET_ALL_LITE_PURCHASE_INTENT, {
      fetchPolicy: 'network-only',
    });

  const handleNextPage = () => {
    let litePurchaseIntentCursorInput: LitePurchaseIntentCursorInput = {};
    if (nextCursor) {
      litePurchaseIntentCursorInput = {
        ...litePurchaseIntentCursorInput,
        createdAt: nextCursor.createdAt,
        purchaseIntentId: nextCursor.purchaseIntentId,
        insuranceProducts: true,
      };
    }

    listPurchaseIntent({
      variables: {
        first: pageSize,
        after: litePurchaseIntentCursorInput,
      },
    });
    setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    let litePurchaseIntentCursorInput: LitePurchaseIntentCursorInput = {};
    if (currentPage > 1) {
      litePurchaseIntentCursorInput = {
        ...litePurchaseIntentCursorInput,
        createdAt: cursorList[currentPage - 2].createdAt,
        purchaseIntentId: cursorList[currentPage - 2].purchaseIntentId,
        insuranceProducts: true,
      };
    }

    listPurchaseIntent({
      variables: {
        first: pageSize,
        after: litePurchaseIntentCursorInput,
      },
    });
    setCurrentPage(currentPage - 1);
  };

  useEffect(() => {
    listPurchaseIntent({
      variables: {
        first: pageSize,
        after: {
          insuranceProducts: true,
        },
      },
    });
  }, []);

  const handleLitePurchaseIntentData = () => {
    if (liteData && liteData.paginateLitePurchaseIntent) {
      const litePurchaseIntents = liteData.paginateLitePurchaseIntent.edges.map(
        (edge) => edge.node,
      );

      setLitePurchaseIntentList(litePurchaseIntents);
      setNextCursor(liteData.paginateLitePurchaseIntent.pageInfo.endCursor);
      setHasNextPage(liteData.paginateLitePurchaseIntent.pageInfo.hasNextPage);
      let page = currentPage;
      if (currentPage === -1) {
        setCurrentPage(0);
        page = 0;
      }
      setHasPrevPage(page > 0);
      if (
        liteData.paginateLitePurchaseIntent.pageInfo.endCursor &&
        page + 1 > cursorList.length
      ) {
        setCursorList([
          ...cursorList,
          liteData.paginateLitePurchaseIntent.pageInfo.endCursor,
        ]);
      }
    }
  };

  useEffect(() => {
    const litePurchaseIntentCursorInput: LitePurchaseIntentCursorInput = {
      insuranceProducts: true,
    };

    listPurchaseIntent({
      variables: {
        first: pageSize,
        after: litePurchaseIntentCursorInput,
      },
    });
    setCurrentPage(0);
    setCursorList([]);
  }, [pageSize]);

  useEffect(() => {
    handleLitePurchaseIntentData();
  }, [liteData, liteLoading]);

  const getProposalState = (litePurchaseIntent: LitePurchaseIntent) => {
    if (litePurchaseIntent.insurance?.insuranceProposal) {
      return 'Propuesta creada';
    }

    return 'Sin propuesta';
  };

  const columns: ColumnsType<LitePurchaseIntent> = [
    {
      title: 'Fecha',
      align: 'center',
      dataIndex: 'createdAt',
      render: (createdAt: string) => <p>{dateFormatter(createdAt)}</p>,
      sorter: (a: LitePurchaseIntent, b: LitePurchaseIntent) =>
        (a.createdAt || '').toString().localeCompare(b.createdAt || ''),
      defaultSortOrder: 'descend',
    },
    {
      title: 'N° simulación',
      align: 'center',
      render: (litePurchaseIntent: LitePurchaseIntent) => (
        <Button
          mode="text"
          onClick={() => {
            localStorage.setItem(
              'customerRut',
              litePurchaseIntent.customerRut || '',
            );
            localStorage.setItem(
              'purchaseIntentId',
              litePurchaseIntent.purchaseIntentId,
            );
            localStorage.setItem('dealer', getDealerFromIdToken() || '');
            history.push({
              pathname: '/insuranceSimulation',
              state: {
                from: history.location,
              },
            });
            localStorage.setItem('detailsInViewMode', 'true');
          }}
        >
          {litePurchaseIntent.purchaseIntentId}
        </Button>
      ),
    },
    {
      title: 'Rut cliente',
      align: 'center',
      dataIndex: 'customerRut',
      render: (clientRut: string) =>
        validateRut(clientRut) ? formatRut(clientRut) : clientRut,
      sorter: (a: LitePurchaseIntent, b: LitePurchaseIntent) =>
        (a.customerRut as string).localeCompare(b.customerRut as string),
    },
    {
      title: 'Modelo',
      align: 'center',
      dataIndex: 'car',
      render: (car: Car) => car.model,
    },
    {
      title: 'Estado',
      align: 'center',
      render: (litePurchaseIntent: LitePurchaseIntent) => (
        <p>{getProposalState(litePurchaseIntent)}</p>
      ),
    },
    {
      align: 'right',
      render: (litePurchaseIntent: LitePurchaseIntent) => (
        <Button
          mode="text"
          onClick={() => {
            localStorage.setItem(
              'customerRut',
              litePurchaseIntent.customerRut || '',
            );
            localStorage.setItem(
              'purchaseIntentId',
              litePurchaseIntent.purchaseIntentId,
            );
            localStorage.setItem('dealer', getDealerFromIdToken() || '');
            history.push({
              pathname: `/insuranceProposal/${litePurchaseIntent.purchaseIntentId}`,
              state: {
                from: history.location,
              },
            });
          }}
        >
          Detalles
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 96px' }}>
      <Row justify="space-between" align="middle">
        <Modal
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          centered
          width="30%"
          closable={false}
          bodyStyle={{ padding: '0', borderRadius: '12px' }}
          maskStyle={{ backdropFilter: 'blur(8px)' }}
        >
          <PurchaseIntentSearch insuranceSearch />
        </Modal>
        <PageHeader title="Simulaciones" />

        <Row>
          <Button
            icon={<SearchOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Buscar
          </Button>
        </Row>
      </Row>

      <Row justify="center" className={styles.TableContainer}>
        <Table
          loading={liteLoading}
          columns={columns}
          dataSource={litePurchaseIntentList || []}
          rowKey={(record) => record.purchaseIntentId}
          pagination={false}
        />
      </Row>
      <Row justify="end" className={styles.PaginationContainer}>
        <Pagination
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          showSizeChanger
        />
      </Row>
    </div>
  );
};

export default InsuranceProposal;
